import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import useAuthStore from './store/authStore';
import Login from './pages/Login';
import Layout from './components/shared/Layout';
import Dashboard from './pages/Dashboard';
import Departments from './pages/Departments';
import PatientRegistration from './pages/opd/PatientRegistration';
import PatientSearch from './pages/opd/PatientSearch';
import PatientDetail from './pages/opd/PatientDetail';
import OPDRegistration from './pages/opd/OPDRegistration';
import IPDCensus from './pages/ipd/IPDCensus';
import IPDPatient from './pages/ipd/IPDPatient';
import IPDRegistration from './pages/ipd/IPDRegistration';
import WardManagement from './pages/ipd/WardManagement';
import IPDPatientsReport from './pages/ipd/IPDPatientsReport';
import WardInventoryRequest from './pages/ipd/WardInventoryRequest';
import IPDBilling from './pages/ipd/IPDBilling';
import IPDBillingReport from './pages/ipd/IPDBillingReport';
import PatientHistory from './pages/patients/PatientHistory';
import RadiologyRegistration from './pages/pacs/RadiologyRegistration';
import RadiologyDashboard from './pages/pacs/RadiologyDashboard';
import RadiologyBilling from './pages/pacs/RadiologyBilling';
import AEDashboard from './pages/ae/AEDashboard';
import AEClinical from './pages/ae/AEClinical';
import AEReports from './pages/ae/AEReports';
import LabWorklist from './pages/lab/LabWorklist';
import LabNewOrder from './pages/lab/LabNewOrder';
import LabInventory from './pages/lab/LabInventory';
import LabParameterSettings from './pages/lab/LabParameterSettings';
import SampleCollection from './pages/lab/SampleCollection';
import LabDashboard from './pages/lab/LabDashboard';
import LabQCLog from './pages/lab/LabQCLog';
import LabSampleTracking from './pages/lab/LabSampleTracking';
import PACSWorklist from './pages/pacs/PACSWorklist';
import PACSNewOrder from './pages/pacs/PACSNewOrder';
import RadiologyReporting from './pages/pacs/RadiologyReporting';
import RadiologyApproval from './pages/pacs/RadiologyApproval';
import DicomViewer from './pages/pacs/DicomViewer';
import OPDDoctorView from './pages/opd/OPDDoctorView';
import Appointments from './pages/opd/Appointments';
import PatientHistory360 from './pages/opd/PatientHistory360';
import OPDBilling from './pages/opd/OPDBilling';
import OPDReports from './pages/opd/OPDReports';
import OPDQueueDisplay from './pages/opd/QueueDisplay';
import PsychCaseload from './pages/psychiatry/PsychCaseload';
import PsychNewCase from './pages/psychiatry/PsychNewCase';
import PsychCaseDetail from './pages/psychiatry/PsychCaseDetail';
import PsychSupervision from './pages/psychiatry/PsychSupervision';
import RoleRoute from './components/shared/RoleRoute';
import Reports from './pages/reports/Reports';
import UserManagement from './pages/admin/UserManagement';
import QueueManage from './pages/queue/QueueManage';
import QueueDisplay from './pages/queue/QueueDisplay';
import PhysioWorklist from './pages/physio/PhysioWorklist';
import PhysioDetail from './pages/physio/PhysioDetail';
import ICUCensus from './pages/icu/ICUCensus';
import ICUPatient from './pages/icu/ICUPatient';
import PharmacyDashboard from './pages/pharmacy/PharmacyDashboard';
import PharmacyDispense from './pages/pharmacy/PharmacyDispense';
import PharmacyHistory from './pages/pharmacy/PharmacyHistory';
import BloodBankDashboard from './pages/bloodbank/BloodBankDashboard';
import BloodBankDonors from './pages/bloodbank/BloodBankDonors';
import BloodBankIssue from './pages/bloodbank/BloodBankIssue';

const PrivateRoute = ({ children }) => {
    const token = useAuthStore((s) => s.token);
    return token ? children : <Navigate to="/login" />;
};

export default function App() {
    const { token, fetchMe } = useAuthStore();

    useEffect(() => {
        if (token) fetchMe();
    }, [token]);

    return (
        <BrowserRouter>
            <ToastContainer position="top-right" autoClose={3000} />
            <Routes>
                <Route path="/login" element={<Login />} />
                {/* OPD Queue Display — public TV screen, no auth required */}
                <Route path="/opd/queue-display" element={<OPDQueueDisplay />} />
                <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
                    <Route index element={<Dashboard />} />
                    <Route path="departments" element={<Departments />} />

                    {/* Reports */}
                    <Route path="reports" element={<RoleRoute module="reports"><Reports /></RoleRoute>} />

                    {/* User Management */}
                    <Route path="admin/users" element={<RoleRoute module="user_mgmt"><UserManagement /></RoleRoute>} />

                    {/* OPD */}
                    <Route path="opd/appointments" element={<RoleRoute module="opd"><Appointments /></RoleRoute>} />
                    <Route path="opd/register" element={<RoleRoute module="opd"><OPDRegistration /></RoleRoute>} />
                    <Route path="opd/doctor-view" element={<RoleRoute module="opd"><OPDDoctorView /></RoleRoute>} />
                    <Route path="opd/doctor" element={<RoleRoute module="opd"><OPDDoctorView /></RoleRoute>} />
                    <Route path="opd/billing" element={<RoleRoute module="opd"><OPDBilling /></RoleRoute>} />
                    <Route path="opd/reports" element={<RoleRoute module="opd"><OPDReports /></RoleRoute>} />
                    <Route path="opd/patient/:patient_id/history" element={<RoleRoute module="opd"><PatientHistory360 /></RoleRoute>} />

                    {/* Patients */}
                    <Route path="patients/register" element={<RoleRoute module="patients"><PatientRegistration /></RoleRoute>} />
                    <Route path="patients/search" element={<RoleRoute module="patients"><PatientSearch /></RoleRoute>} />
                    <Route path="patients/:id" element={<RoleRoute module="patients"><PatientDetail /></RoleRoute>} />

                    {/* IPD */}
                    <Route path="ipd" element={<RoleRoute module="ipd"><IPDRegistration /></RoleRoute>} />
                    <Route path="ipd/wards" element={<RoleRoute module="ipd"><WardManagement /></RoleRoute>} />
                    <Route path="ipd/ward-request" element={<RoleRoute module="ipd"><WardInventoryRequest /></RoleRoute>} />
                    <Route path="ipd/billing" element={<RoleRoute module="ipd"><IPDBilling /></RoleRoute>} />
                    <Route path="ipd/billing-report" element={<RoleRoute module="ipd"><IPDBillingReport /></RoleRoute>} />
                    <Route path="ipd/patients-report" element={<RoleRoute module="ipd"><IPDPatientsReport /></RoleRoute>} />
                    <Route path="patients/history" element={<RoleRoute module="ipd"><PatientHistory /></RoleRoute>} />
                    <Route path="ipd/register" element={<RoleRoute module="ipd"><IPDRegistration /></RoleRoute>} />
                    <Route path="ipd/admit" element={<Navigate to="/ipd" replace />} />
                    <Route path="ipd/:id" element={<RoleRoute module="ipd"><IPDPatient /></RoleRoute>} />

                    {/* A&E */}
                    <Route path="ae" element={<RoleRoute module="emergency"><AEDashboard /></RoleRoute>} />
                    <Route path="ae/new" element={<Navigate to="/ae" replace />} />
                    <Route path="ae/clinical/:visit_id" element={<RoleRoute module="emergency"><AEClinical /></RoleRoute>} />
                    <Route path="ae/reports" element={<RoleRoute module="emergency"><AEReports /></RoleRoute>} />

                    {/* Lab */}
                    <Route path="lab/dashboard" element={<RoleRoute module="lab"><LabDashboard /></RoleRoute>} />
                    <Route path="lab/worklist" element={<RoleRoute module="lab"><LabWorklist /></RoleRoute>} />
                    <Route path="lab/order" element={<RoleRoute module="lab"><LabNewOrder /></RoleRoute>} />
                    <Route path="lab/inventory" element={<RoleRoute module="lab"><LabInventory /></RoleRoute>} />
                    <Route path="lab/parameters" element={<RoleRoute module="lab"><LabParameterSettings /></RoleRoute>} />
                    <Route path="lab/sample-collection" element={<RoleRoute module="lab"><SampleCollection /></RoleRoute>} />
                    <Route path="lab/qc" element={<RoleRoute module="lab"><LabQCLog /></RoleRoute>} />
                    <Route path="lab/sample-tracking" element={<RoleRoute module="lab"><LabSampleTracking /></RoleRoute>} />

                    {/* Radiology / PACS */}
                    <Route path="pacs/register" element={<RoleRoute module="radiology"><RadiologyRegistration /></RoleRoute>} />
                    <Route path="pacs/dashboard" element={<RoleRoute module="radiology"><RadiologyDashboard /></RoleRoute>} />
                    <Route path="pacs/billing" element={<RoleRoute module="radiology"><RadiologyBilling /></RoleRoute>} />
                    <Route path="pacs/worklist" element={<RoleRoute module="radiology"><PACSWorklist /></RoleRoute>} />
                    <Route path="pacs/order" element={<RoleRoute module="radiology"><PACSNewOrder /></RoleRoute>} />
                    <Route path="pacs/reporting" element={<RoleRoute module="radiology"><RadiologyReporting /></RoleRoute>} />
                    <Route path="pacs/approval" element={<RoleRoute module="radiology"><RadiologyApproval /></RoleRoute>} />
                    <Route path="pacs/dicom" element={<RoleRoute module="radiology"><DicomViewer /></RoleRoute>} />

                    {/* Queue */}
                    <Route path="queue/manage" element={<RoleRoute module="queue"><QueueManage /></RoleRoute>} />
                    <Route path="queue/display" element={<RoleRoute module="queue"><QueueDisplay /></RoleRoute>} />

                    {/* Physiotherapy */}
                    <Route path="physio" element={<RoleRoute module="physio"><PhysioWorklist /></RoleRoute>} />
                    <Route path="physio/:id" element={<RoleRoute module="physio"><PhysioDetail /></RoleRoute>} />

                    {/* ICU / CCU */}
                    <Route path="icu" element={<RoleRoute module="icu"><ICUCensus /></RoleRoute>} />
                    <Route path="icu/:id" element={<RoleRoute module="icu"><ICUPatient /></RoleRoute>} />

                    {/* Pharmacy */}
                    <Route path="pharmacy" element={<RoleRoute module="pharmacy"><PharmacyDashboard /></RoleRoute>} />
                    <Route path="pharmacy/dispense" element={<RoleRoute module="pharmacy"><PharmacyDispense /></RoleRoute>} />
                    <Route path="pharmacy/history" element={<RoleRoute module="pharmacy"><PharmacyHistory /></RoleRoute>} />
                    <Route path="blood-bank" element={<RoleRoute module="blood_bank"><BloodBankDashboard /></RoleRoute>} />
                    <Route path="blood-bank/donors" element={<RoleRoute module="blood_bank"><BloodBankDonors /></RoleRoute>} />
                    <Route path="blood-bank/issue" element={<RoleRoute module="blood_bank"><BloodBankIssue /></RoleRoute>} />

                    {/* Psychiatry */}
                    <Route path="psych" element={<RoleRoute module="psychiatry"><PsychCaseload /></RoleRoute>} />
                    <Route path="psych/new" element={<RoleRoute module="psychiatry"><PsychNewCase /></RoleRoute>} />
                    <Route path="psych/cases" element={<RoleRoute module="psychiatry"><PsychCaseload /></RoleRoute>} />
                    <Route path="psych/cases/:id" element={<RoleRoute module="psychiatry"><PsychCaseDetail /></RoleRoute>} />
                    <Route path="psych/supervision" element={<RoleRoute module="psychiatry"><PsychSupervision /></RoleRoute>} />
                </Route>
            </Routes>
        </BrowserRouter>
    );
}
